import React, { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import * as Actions from 'state/actions'
import Emitter from 'shared/utils/emitter'
import history from 'shared/utils/history'
import { getToken, isLoggedIn, isTokenCloseToExpiration } from 'shared/utils/token'
import {
  beginLogin,
  completeLogin,
  hasAuthCode,
  getAuthError,
  clearAuthError,
  refreshTokens,
  logout as cognitoLogout
} from 'shared/utils/cognito-auth'

// How often to check whether the id token is due for renewal. The id token
// lives 15 minutes, so the check has to run well inside that.
const REFRESH_CHECK_INTERVAL_MS = 1000 * 60 * 2
const currentPath = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`

export const AuthContext = React.createContext()
export const useAuth = () => useContext(AuthContext)
export const AuthProvider = ({
  children
}) => {
  //redux hook
  const dispatch = useDispatch()

  const [ isAuthenticated, setIsAuthenticated ] = useState(false)
  const [ accessToken, setAccessToken ] = useState(null)
  const [ idToken, setIdToken ] = useState(null)
  const [ loading, setLoading ] = useState(true)
  const [ authError, setAuthError ] = useState(null)

  const applyToken = token => {
    setIdToken(token)
    setIsAuthenticated(true)
    dispatch(Actions.setToken(token))
    dispatch(Actions.fetchProfile())
  }

  // Cognito hosted-UI path.
  //
  // Exchange the ?code= on the callback, persist the id token, and on later
  // loads read it back from storage. PrivateRoute starts the redirect when
  // neither applies.
  //
  // There is no signup here: the hosted UI this client is configured for is
  // login-only.
  useEffect(() => {
    // A rejected login comes back as ?error=, not ?code=. Surface it: the pool
    // is invitation-only, so "Registration is by invitation only" is the single
    // most likely thing a real user sees here, and without this branch it
    // renders as a silent bounce back to the login screen.
    const returnedError = getAuthError()
    if (returnedError) {
      console.error('Cognito :: login rejected', returnedError)
      setAuthError(returnedError)
      clearAuthError()
      setLoading(false)
      return
    }

    if (hasAuthCode()) {
      completeLogin()
        .then(({ id_token, access_token, path }) => {
          // Restore the pre-login path through the router's own history, not
          // window.history: the router mounted on the callback URL, so a bare
          // replaceState would leave it matching the wrong route.
          if (path && path !== currentPath()) history.replace(path)

          if (access_token) setAccessToken(access_token)
          applyToken(id_token)
        })
        .catch(error => {
          console.error('Cognito :: failed to complete login', error)
          setAuthError({
            error: 'code_exchange_failed',
            description: error.message,
            message: 'Something went wrong while signing you in. Please try again.'
          })
        })
        .finally(() => setLoading(false))
      return
    }

    // No code on the URL: a valid stored token means the user is already signed
    // in; otherwise PrivateRoute calls login() and the redirect starts.
    if (isLoggedIn()) {
      // isLoggedIn() only asks whether the token has expired, so a token inside
      // the renewal window still passes it. Applying that token would fire
      // fetchProfile and its success-actions against a credential with seconds
      // left, and the renewal timer below does not run until its first tick —
      // so the app renders signed-out until it does. Renew first when one is due.
      let due
      try {
        due = isTokenCloseToExpiration()
      } catch (error) {
        due = false
      }

      if (due) {
        refreshTokens()
          .then(({ id_token }) => applyToken(id_token))
          .catch(error => {
            // The refresh token is gone or revoked, so the session is over.
            // PrivateRoute sends the user to the hosted UI from here.
            console.error('Cognito :: failed to renew the stored session', error)
          })
          .finally(() => setLoading(false))
        return
      }

      applyToken(getToken())
    }
    setLoading(false)
  }, [])

  /**
   * Run the refresh-token grant. A failure means the 48-hour refresh token is
   * gone or revoked, so the session is genuinely over and the only correct
   * move is a clean trip back to the hosted UI.
   */
  const refresh = () =>
    refreshTokens()
      .then(({ id_token }) => {
        setIdToken(id_token)
        dispatch(Actions.setToken(id_token))
      })
      .catch(error => {
        // A transient failure (offline, laptop just woke, Cognito 5xx) must NOT
        // end the session: a hard logout here also wipes the shared same-origin
        // dashboard session and strands the user on the hosted /logout, even
        // though the 48-hour refresh token is still valid. Log it and let the
        // next renewal tick retry — matching the removed Auth0 path, which
        // logged out only on login_required. Only a genuinely dead refresh
        // token (400 invalid_grant, or none stored) ends the session.
        if (error && error.transient) {
          console.warn('Cognito :: transient refresh failure; will retry on the next tick', error)
          return
        }

        console.error('Cognito :: refresh token rejected, ending session', error)
        if (isAuthenticated) {
          setIsAuthenticated(false)
          cognitoLogout()
        }
      })

  // Renew the id token before it expires. Nothing scheduled a renewal before,
  // so the id token simply died 15 minutes into the session and the next
  // request 401'd.
  useEffect(() => {
    if (!isAuthenticated) return

    const timer = setInterval(() => {
      // isTokenCloseToExpiration throws when no token is stored, which is a
      // signed-out tab rather than an error worth surfacing.
      let due
      try {
        due = isTokenCloseToExpiration()
      } catch (error) {
        return
      }

      if (due) refresh()
    }, REFRESH_CHECK_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [ isAuthenticated ])

  // The gateway can also ask the app to re-check its session over the socket.
  // Registered in an effect so the subscription is added once and removed on
  // unmount — at render scope it re-subscribed on every render and leaked.
  useEffect(() => {
    const onCheckSession = () => refresh()

    Emitter.on('auth.check.session', onCheckSession)
    return () => Emitter.off('auth.check.session', onCheckSession)
  }, [ isAuthenticated ])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        accessToken,
        idToken,
        loading,
        authError,

        login: options => {
          setAuthError(null)
          return beginLogin(options)
        },
        logout: () => {
          dispatch(Actions.authCheckSession())
          cognitoLogout()
        }
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
AuthProvider.propTypes = {
  children: PropTypes.any
}
